import { z } from 'zod';

// General activities: the person × activity grid in Projects & tasks where team
// approvers ("leads" — approver membership of the team, not an app role; I-02)
// decide who gets each category assigned by leads (`visibility: 'leads'`).
//
// The rule every shape below reports on, stated once (REQUIREMENTS.md U-02):
// a person is GRANTED a lead-assigned category when they have an individual
// choice saying granted; with no individual choice, when any team they belong
// to has the category set to "whole team". The server computes it with one
// shared predicate; nothing here is for the client to re-derive.
//
// Every field the UI acts on is server-computed, including `editable`.
// Authorization is never inferred from a display field (see
// docs/solutions/conventions/client-only-rule-is-not-enforced-2026-09-02.md).
// Hours are minutes on the wire, as everywhere else in this API; format them
// with `formatHours` from this package.

/**
 * Why a cell reads the way it does.
 *
 * - `individual` — the person has an individual choice for this category; it
 *   decides the cell either way (granted, or removed). `setBy` names who made it.
 * - `team`       — no individual choice, and a team of theirs is set to "whole
 *   team"; granted through the team.
 *
 * `null` on a cell means neither: not granted, and nothing ever decided so —
 * no choice and no whole-team team.
 */
export const GRANT_REASONS = ['team', 'individual'] as const;
export const GrantReason = z.enum(GRANT_REASONS);
export type GrantReason = z.infer<typeof GrantReason>;

/** A lead-assigned category, one grid column. Active categories only. */
export const GeneralActivityColumn = z.object({
  categoryId: z.string().uuid(),
  name: z.string(),
  // Work time (Indirect) vs non-work (Leave) — the column's type label.
  isWorkTime: z.boolean(),
});
export type GeneralActivityColumn = z.infer<typeof GeneralActivityColumn>;

/** One person × activity cell. */
export const GeneralActivityCell = z.object({
  categoryId: z.string().uuid(),
  // The effective grant — what the person's timesheet actually gets.
  granted: z.boolean(),
  reason: GrantReason.nullable(),
  // Who made the individual choice, by display name. Non-null only when
  // `reason` is `individual` and the setter still exists; null there means the
  // setter's user row was deleted ("added individually", no name).
  setBy: z.string().nullable(),
  // When `reason` is `team`: the names of the person's teams whose "whole
  // team" setting grants it (the "via A + B" sub-line), name-ascending —
  // including teams this viewer cannot see. Empty for any other reason.
  viaTeams: z.array(z.string()),
  // Whether the person holds a line for this category with time entries or a
  // note on any editable (draft/returned) timesheet — a row that would stay,
  // badged, if they lost access. Every such week, not only the current one
  // (divergence D5: "timesheets not yet submitted").
  hasHoursOrNote: z.boolean(),
  // Total minutes on those lines. Null when `hasHoursOrNote` is false; 0 is a
  // real value (a note-only row, or a 0-minute entry).
  minutes: z.number().int().nonnegative().nullable(),
  // Whether THIS viewer may write this person's choice — server-computed from
  // authorization (approver of one of the person's teams, or administrator).
  editable: z.boolean(),
});
export type GeneralActivityCell = z.infer<typeof GeneralActivityCell>;

/** A person row. A two-team person appears under each of their visible teams. */
export const GeneralActivityMember = z.object({
  userId: z.string().uuid(),
  displayName: z.string(),
  // The person's UT EID, shown under their name. Null for a user row created
  // without one (it is nullable on `users`).
  utEid: z.string().nullable(),
  // Avatar initials, computed by the API from the AD name parts. Render
  // verbatim; never derive initials client-side.
  initials: z.string(),
  // Names of the person's OTHER teams (the "Also on {Team}" pill and the
  // "via A + B" sub-line), name-ascending. Includes teams this viewer cannot
  // see: the pill is about the person, not the viewer's scope.
  otherTeams: z.array(z.string()),
  // One per grid column, in `activities` order, keyed by categoryId.
  cells: z.array(GeneralActivityCell),
});
export type GeneralActivityMember = z.infer<typeof GeneralActivityMember>;

/** A team's "whole team" setting for one activity (missing row = off). */
export const GeneralActivityTeamSetting = z.object({
  categoryId: z.string().uuid(),
  wholeTeam: z.boolean(),
});
export type GeneralActivityTeamSetting = z.infer<typeof GeneralActivityTeamSetting>;

export const GeneralActivityTeam = z.object({
  teamId: z.string().uuid(),
  name: z.string(),
  color: z.string(),
  // The team lead's display name (`teams.lead_user_id`) for the heading's
  // sub-line; null when the team has no lead.
  leadName: z.string().nullable(),
  // Whether this viewer may write the team's settings (approver of the team, or
  // administrator).
  editable: z.boolean(),
  // One per grid column, in `activities` order.
  settings: z.array(GeneralActivityTeamSetting),
  // Active members only, ordered by display name then user id.
  members: z.array(GeneralActivityMember),
});
export type GeneralActivityTeam = z.infer<typeof GeneralActivityTeam>;

/**
 * The administrator-only "No team" group (divergence D7): active people on no
 * team. They get lead-assigned activities only through an administrator's
 * individual choice, so the group has no team setting and no heading checkbox.
 */
export const GeneralActivityNoTeamGroup = z.object({
  members: z.array(GeneralActivityMember),
});
export type GeneralActivityNoTeamGroup = z.infer<typeof GeneralActivityNoTeamGroup>;

/** A category that is not lead-assigned, for the "set by administrators" chips. */
export const GeneralActivityFixedCategory = z.object({
  categoryId: z.string().uuid(),
  name: z.string(),
});
export type GeneralActivityFixedCategory = z.infer<typeof GeneralActivityFixedCategory>;

/** `GET` the General activities grid — one response, no N+1. */
export const GeneralActivitiesGridResponse = z.object({
  // Active lead-assigned categories, name-ascending then id.
  activities: z.array(GeneralActivityColumn),
  // The viewer's approver teams (administrators: every team), name-ascending
  // then id.
  teams: z.array(GeneralActivityTeam),
  // Administrators only; null for everyone else. Gated in the server's query,
  // never filtered out of a fuller response.
  noTeam: GeneralActivityNoTeamGroup.nullable(),
  // Active categories visible to everyone, and to approvers only — the chip
  // row. Name-ascending then id.
  everyoneCategories: z.array(GeneralActivityFixedCategory),
  approversOnlyCategories: z.array(GeneralActivityFixedCategory),
});
export type GeneralActivitiesGridResponse = z.infer<typeof GeneralActivitiesGridResponse>;

// ---- Impact (preview, mutation result, and "impact changed") ----------------

/** Someone who loses the activity and has no row worth keeping. */
export const GrantImpactLost = z.object({
  userId: z.string().uuid(),
  displayName: z.string(),
});
export type GrantImpactLost = z.infer<typeof GrantImpactLost>;

/** Someone who loses the activity but whose row stays: hours or a note on it. */
export const GrantImpactKeptWithHours = z.object({
  userId: z.string().uuid(),
  displayName: z.string(),
  // Minutes across their editable sheets; 0 with `hasNote` = "note · row stays".
  minutes: z.number().int().nonnegative(),
  hasNote: z.boolean(),
});
export type GrantImpactKeptWithHours = z.infer<typeof GrantImpactKeptWithHours>;

/**
 * Someone whose "removed" choice a team-on cleared. `removedByName` is the
 * person who made that choice (divergence D1), null when their user row is gone.
 */
export const GrantImpactOverridden = z.object({
  userId: z.string().uuid(),
  displayName: z.string(),
  removedByName: z.string().nullable(),
});
export type GrantImpactOverridden = z.infer<typeof GrantImpactOverridden>;

/**
 * Someone a team-off leaves untouched because they still have the activity
 * another way: "{Name} keeps it through {Team}" or "{Name} keeps it — added
 * individually by {Lead}".
 */
export const GrantImpactKeepsIt = z.discriminatedUnion('reason', [
  z.object({
    userId: z.string().uuid(),
    displayName: z.string(),
    reason: z.literal(GrantReason.enum.team),
    viaTeamName: z.string(),
  }),
  z.object({
    userId: z.string().uuid(),
    displayName: z.string(),
    reason: z.literal(GrantReason.enum.individual),
    // Null when the setter's user row is gone ("added individually").
    byName: z.string().nullable(),
  }),
]);
export type GrantImpactKeepsIt = z.infer<typeof GrantImpactKeepsIt>;

/** Another team a person is on, for the "Also on another team" panel. */
export const GrantImpactAlsoOnTeam = z.object({
  teamId: z.string().uuid(),
  teamName: z.string(),
  // The team's lead (`teams.lead_user_id`), null when it has none.
  leadName: z.string().nullable(),
});
export type GrantImpactAlsoOnTeam = z.infer<typeof GrantImpactAlsoOnTeam>;

/**
 * What a grant write does, split by outcome. ONE shape for three uses, because
 * the server computes all three with one function (a preview is a read-only
 * invocation of the commit's computation), so there is no second
 * implementation to drift:
 *
 *  - an impact PREVIEW for a person untick or a team off (the dialog's panels);
 *  - the MUTATION RESULT of every person or team write (the toast);
 *  - the fresh preview inside a `grant_impact_changed` 409.
 *
 * Lists not relevant to an action are empty: `overridden` is only ever filled
 * by a team on, `keepsIt` by a team off, `alsoOnTeams` by a person untick.
 * `lost` and `keptWithHours` are disjoint and together are exactly the people
 * who lose access ("Remove from {n} people" counts them).
 */
export const GeneralActivityGrantImpact = z.object({
  lost: z.array(GrantImpactLost),
  keptWithHours: z.array(GrantImpactKeptWithHours),
  overridden: z.array(GrantImpactOverridden),
  keepsIt: z.array(GrantImpactKeepsIt),
  alsoOnTeams: z.array(GrantImpactAlsoOnTeam),
});
export type GeneralActivityGrantImpact = z.infer<typeof GeneralActivityGrantImpact>;

/**
 * The sets a confirm dialog was shown, sent back with the write so the server
 * can refuse with `grant_impact_changed` if its in-transaction recomputation
 * disagrees. User-id sets, compared order-insensitively: a reordered list is
 * the same decision, a different person is not.
 *
 * Send all three exactly as the preview returned them. The server compares the
 * ones that matter to the action: `lostUserIds` and `keptWithHoursUserIds` for
 * a removal, `overriddenUserIds` for a team on.
 */
export const AcknowledgedGrantImpact = z.object({
  lostUserIds: z.array(z.string().uuid()),
  keptWithHoursUserIds: z.array(z.string().uuid()),
  overriddenUserIds: z.array(z.string().uuid()),
});
export type AcknowledgedGrantImpact = z.infer<typeof AcknowledgedGrantImpact>;

/**
 * Write a person's individual choice. The choice applies everywhere — on every
 * team the person is on — and the most recent choice wins.
 *
 * `acknowledged` is required when the untick came from the confirm dialog (the
 * person has hours or a note, or another team grants it) and omitted for an
 * immediate tick or untick.
 */
export const SetGeneralActivityPersonRequest = z
  .object({
    categoryId: z.string().uuid(),
    userId: z.string().uuid(),
    granted: z.boolean(),
    acknowledged: AcknowledgedGrantImpact.optional(),
  })
  .strict();
export type SetGeneralActivityPersonRequest = z.infer<typeof SetGeneralActivityPersonRequest>;

/**
 * Turn a team's "whole team" setting on or off.
 *
 * On clears every "removed" choice among the team's members (two-team members
 * included). Sent without `acknowledged`, a team on that would override another
 * lead's removals is refused with `grant_impact_changed` carrying the
 * `overridden` list — that 409 IS the confirmation prompt. Off clears every
 * choice of members on this team only and always comes from the dialog, so it
 * carries `acknowledged`.
 */
export const SetGeneralActivityTeamRequest = z
  .object({
    categoryId: z.string().uuid(),
    teamId: z.string().uuid(),
    wholeTeam: z.boolean(),
    acknowledged: AcknowledgedGrantImpact.optional(),
  })
  .strict();
export type SetGeneralActivityTeamRequest = z.infer<typeof SetGeneralActivityTeamRequest>;

/**
 * What a person write returns: the person's resulting cell for the category
 * (as the grid would now show it to this viewer) and what the write did. The
 * toast reads `impact`: `lost` / `keptWithHours` for an untick that removed
 * access, nothing for a tick.
 */
export const SetGeneralActivityPersonResponse = z.object({
  cell: GeneralActivityCell,
  impact: GeneralActivityGrantImpact,
});
export type SetGeneralActivityPersonResponse = z.infer<typeof SetGeneralActivityPersonResponse>;

/**
 * What a team write returns: the team's resulting "whole team" setting and what
 * the write did — `overridden` for a team on (the D1 toast), `lost` /
 * `keptWithHours` / `keepsIt` for a team off.
 */
export const SetGeneralActivityTeamResponse = z.object({
  categoryId: z.string().uuid(),
  teamId: z.string().uuid(),
  wholeTeam: z.boolean(),
  impact: GeneralActivityGrantImpact,
});
export type SetGeneralActivityTeamResponse = z.infer<typeof SetGeneralActivityTeamResponse>;

/** Query for the person-untick impact preview. */
export const GeneralActivityPersonImpactQuery = z.object({
  categoryId: z.string().uuid(),
  userId: z.string().uuid(),
});
export type GeneralActivityPersonImpactQuery = z.infer<typeof GeneralActivityPersonImpactQuery>;

/** Query for the team-off impact preview. */
export const GeneralActivityTeamImpactQuery = z.object({
  categoryId: z.string().uuid(),
  teamId: z.string().uuid(),
});
export type GeneralActivityTeamImpactQuery = z.infer<typeof GeneralActivityTeamImpactQuery>;

/**
 * The 409 body when a confirmed impact no longer holds. `code` is always
 * `GRANT_IMPACT_CHANGED` from this package's index (typed as a string here only
 * because the constant lives in the module that re-exports this one). Nothing
 * was written; the dialog re-renders `preview` and asks again.
 */
export const GeneralActivityImpactChangedResponse = z.object({
  error: z.string(),
  code: z.string(),
  preview: GeneralActivityGrantImpact,
});
export type GeneralActivityImpactChangedResponse = z.infer<
  typeof GeneralActivityImpactChangedResponse
>;
